import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { App, Button, Dropdown } from "antd";
import type { TableProps } from "antd";
import {
  useCallback,
  useMemo,
  useState,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import CrossIcon from "sipli-fleet-core/assets/icons/cross-icon.svg";
import DeleteIcon from "sipli-fleet-core/assets/icons/delete-icon.svg";
import EditIcon from "sipli-fleet-core/assets/icons/edit-icon.svg";
import MenuIcon from "sipli-fleet-core/assets/icons/menu-icon.svg";
import SearchIcon from "sipli-fleet-core/assets/icons/search-icon.svg";
import {
  CustomButton,
  CustomTable,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedInput from "sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import {
  useMutationDeleteCustomerType,
  useMutationUpdateCustomerType,
  useQueryGetCustomerTypeColumns,
  useQueryGetCustomerTypesList,
} from "src/queries/administrationSetup.queries";
import type { GeneralSetupRecord, SetupListParams } from "src/services/administrationSetupService";
import { hasAdministrationPermission } from "src/util/administrationPermission";
import { getTotalCount, isActive } from "../setupUtils";
import CustomerTypeAddEdit from "./CustomerTypeAddEdit";
import CustomerTypeDelete from "./CustomerTypeDelete";

type SetupSorting = { sortBy: string; sort: "ASC" | "DESC" };
const Icon = (asset: unknown) => asset as ComponentType<{ className?: string }>;
const CrossIconComponent = Icon(CrossIcon);
const DeleteIconComponent = Icon(DeleteIcon);
const EditIconComponent = Icon(EditIcon);
const MenuIconComponent = Icon(MenuIcon);
const SearchIconComponent = Icon(SearchIcon);
const { CustomText } = CustomTypography;
const DEFAULT_PAGINATION = { page: 0, size: 15 };
const DEFAULT_SORTING: SetupSorting = { sortBy: "ID", sort: "DESC" };
const dataIndexToColumnId: Record<string, string> = {
  name: "NAME",
  code: "CODE",
  description: "DESCRIPTION",
  status: "STATUS",
};
const text = (value: unknown) => (typeof value === "string" && value.trim() ? value : "--");
const StatusBadge = ({ active }: { active?: boolean | number }) => {
  const { t } = useTranslation();
  return (
    <CustomTag
      className={
        isActive(active)
          ? "bg-green-50 text-green-600"
          : "bg-gray-50 text-gray-400"
      }
    >
      {isActive(active) ? t("active") : t("inactive")}
    </CustomTag>
  );
};

const CustomerTypeList = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<SetupSorting>(DEFAULT_SORTING);
  const [search, setSearch] = useState("");
  const [formRecord, setFormRecord] = useState<GeneralSetupRecord | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState<GeneralSetupRecord | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | number | null>(null);
  const [openActionId, setOpenActionId] = useState<string | number | null>(null);
  const canEdit = hasAdministrationPermission("edit");
  const canDelete = hasAdministrationPermission("delete");
  const queryParams: SetupListParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      sort: sorting.sort,
      sortBy: sorting.sortBy,
    }),
    [pagination, sorting]
  );
  const listQuery = useQueryGetCustomerTypesList(queryParams);
  const columnsQuery = useQueryGetCustomerTypeColumns();
  const updateMutation = useMutationUpdateCustomerType();
  const deleteMutation = useMutationDeleteCustomerType();
  void columnsQuery.data;
  const response = listQuery.data?.data?.response;
  const records = response?.clientTypes ?? [];
  const filteredRecords = useMemo(() => {
    const value = search.trim().toLowerCase();
    return value
      ? records.filter((record) =>
          [
            record.name,
            record.code,
            record.description,
            isActive(record.active) ? "active" : "inactive",
          ]
            .join(" ")
            .toLowerCase()
            .includes(value)
        )
      : records;
  }, [records, search]);
  const handleStatusChange = useCallback(
    async (record: GeneralSetupRecord) => {
      setStatusUpdatingId(record.id);
      try {
        await updateMutation.mutateAsync({
          id: record.id,
          payload: { active: !isActive(record.active) },
        });
        setOpenActionId(null);
        message.success(t("clientType.statusUpdateSuccess"));
      } catch {
        // The legacy module relies on the global API error notification.
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [message, t, updateMutation]
  );
  const handleDeleteConfirm = async () => {
    if (!deleteRecord) return;
    try {
      await deleteMutation.mutateAsync(deleteRecord.id);
      message.success(t("clientType.deleteSuccess"));
      setDeleteRecord(null);
    } catch {
      message.error(t("common.deleteError"));
    }
  };
  const renderActions = useCallback(
    (record: GeneralSetupRecord): ReactNode => (
      <div className="flex items-center justify-center gap-2 users-actions">
        {canEdit && (
          <CustomTooltip title={t("edit")}>
            <CustomButton
              type="icon"
              buttonSize="small"
              aria-label={t("edit")}
              className="!bg-transparent"
              icon={<EditIconComponent className="w-[16px] h-[16px]" />}
              onClick={(event) => {
                event?.stopPropagation?.();
                setFormRecord(record);
                setIsFormOpen(true);
              }}
            />
          </CustomTooltip>
        )}
        {canDelete && (
          <CustomTooltip title={t("delete")}>
            <CustomButton
              type="icon"
              buttonSize="small"
              className="!bg-transparent"
              aria-label={t("delete")}
              icon={<DeleteIconComponent className="w-[15px] h-[16px]" />}
              onClick={(event) => {
                event?.stopPropagation?.();
                setDeleteRecord(record);
              }}
            />
          </CustomTooltip>
        )}
        {canEdit && (
          <Dropdown
            trigger={["click"]}
            className="!bg-transparent"
            open={openActionId === record.id}
            onOpenChange={(open) => setOpenActionId(open ? record.id : null)}
            dropdownRender={() => (
              <div className="min-w-[180px] overflow-hidden rounded-sm bg-white shadow-[0_6px_16px_rgba(0,0,0,0.16)]">
                <Button
                  type="text"
                  loading={statusUpdatingId === record.id}
                  className="!flex !h-[36px] !w-full !justify-start"
                  onClick={async (event: MouseEvent<HTMLElement>) => {
                    event.stopPropagation();
                    await handleStatusChange(record);
                  }}
                >
                  {isActive(record.active) ? t("inactive") : t("active")}
                </Button>
              </div>
            )}
          >
            <CustomButton
              type="icon"
              buttonSize="small"
              aria-label={t("more_actions")}
              icon={<MenuIconComponent className="w-[3px] h-[16px]" />}
              onClick={(event) => event?.stopPropagation?.()}
            />
          </Dropdown>
        )}
      </div>
    ),
    [canDelete, canEdit, handleStatusChange, openActionId, statusUpdatingId, t]
  );
  const columns: TableProps<GeneralSetupRecord>["columns"] = useMemo(
    () => [
      {
        title: t("client_type_name"),
        dataIndex: "name",
        key: "name",
        sorter: true,
        render: (value: string) => (
          <CustomText className="!text-sm !font-semibold !text-black">{text(value)}</CustomText>
        ),
      },
      { title: t("client_type_code"), dataIndex: "code", key: "code", sorter: true, render: text },
      {
        title: t("description"),
        dataIndex: "description",
        key: "description",
        sorter: true,
        render: (value) => (
          <CustomTooltip title={text(value)}>
            <span className="block max-w-[320px] truncate">{text(value)}</span>
          </CustomTooltip>
        ),
      },
      {
        title: t("status"),
        key: "status",
        sorter: true,
        render: (_, record) => <StatusBadge active={record.active} />,
      },
      ...(canEdit || canDelete
        ? [
            {
              title: t("actions"),
              key: "actions",
              align: "center" as const,
              width: 120,
              render: (_: unknown, record: GeneralSetupRecord) => renderActions(record),
            },
          ]
        : []),
    ],
    [canDelete, canEdit, renderActions, t]
  );
  const handleTableChange: TableProps<GeneralSetupRecord>["onChange"] = (_, __, sorter, extra) => {
    if (extra.action === "filter") return;
    const current = Array.isArray(sorter) ? sorter[0] : sorter;
    if (!current) return;
    const id = current.field || current.columnKey;
    const sortBy = id ? dataIndexToColumnId[String(id)] : undefined;
    setSorting(
      current.order && sortBy
        ? { sortBy, sort: current.order === "ascend" ? "ASC" : "DESC" }
        : DEFAULT_SORTING
    );
    setPagination((value) => ({ ...value, page: 0 }));
  };
  return (
    <section className="pb-8 pl-2 bg-gray1">
      <CustomSharedTitleSection
        titleKey="client_type"
        buttonKey="create"
        onClick={() => {
          setFormRecord(null);
          setIsFormOpen(true);
        }}
        showBtn={canEdit}
        titleClassName="!text-[25.23px] !font-[700]"
      />
      <div className="flex flex-col overflow-hidden bg-white rounded-[16px]">
        <div className="flex px-[18px] -mt-2">
          <div>
            <CustomSharedInput
              allowClear={{ clearIcon: <CrossIconComponent className="w-[8.5px] h-[8.5px]" /> }}
              prefix={<SearchIconComponent className="w-[14px] h-[14px]" />}
              placeholder={t("search")}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPagination((value) => ({ ...value, page: 0 }));
              }}
            />
          </div>
        </div>
        <CustomTable<GeneralSetupRecord>
          data={filteredRecords}
          columns={columns}
          rowKey={(record) => String(record.id)}
          isLoading={listQuery.isLoading}
          pageSize={pagination.size}
          currentPage={pagination.page}
          totalCount={
            search.trim() ? filteredRecords.length : getTotalCount(response, records.length)
          }
          handlePagination={(page) => setPagination((value) => ({ ...value, page }))}
          handlePageSizeChange={(size) => setPagination({ page: 0, size: Number(size) })}
          showSearchInput={false}
          showCustomizeColumns={false}
          showDownload={false}
          onChange={handleTableChange}
          showTopHorizontalScrollbar
          locale={{ emptyText: t("no_data") }}
        />
      </div>
      <CustomerTypeAddEdit
        open={isFormOpen}
        record={formRecord}
        onClose={() => setIsFormOpen(false)}
      />
      <CustomerTypeDelete
        open={Boolean(deleteRecord)}
        isLoading={deleteMutation.isPending}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDeleteConfirm}
      />
    </section>
  );
};

export default CustomerTypeList;
